import apiClient from '@/shared/services/apiClient'
import type {
  DashboardOverviewResponse,
  HighYieldUnitsResponse,
  TopicTrendsSummaryResponse,
  SubjectUnitWeightageResponse,
  SubjectDifficultyTrendsResponse,
  ExamSummaryResponse,
  ExamsListResponse,
  UnitTopicBreakdownResponse,
  HighYieldTopicsResponse,
  PagedTestAttemptResponse,
  TestAttemptStatus,
  TrendingTopicsResponse,
  MustStudyTopicsResponse,
  NextExamPredictionsResponse,
  UnitSummaryResponse,
  UnitExamTimelineResponse,
  TopicDetailedStatsResponse,
  TopicExamTimelineResponse,
  TopicComparisonRequest,
  TopicComparisonResponse,
  ExamTopicCoverageResponse,
  YearOverYearComparisonResponse,
} from '../types'

const API_BASE = '/api/v1/pyq-analytics'
const TEST_ATTEMPTS_API_BASE = '/api/v1/test-attempts'

export interface YearRangeParams {
  yearRangeStart?: number
  yearRangeEnd?: number
}

class AnalyticsService {
  // Dashboard Endpoints
  async getDashboardOverview(params?: YearRangeParams): Promise<DashboardOverviewResponse> {
    const response = await apiClient.get<DashboardOverviewResponse>(
      `${API_BASE}/dashboard/overview`,
      { params }
    )
    return response.data
  }

  async getHighYieldUnits(params?: {
    limit?: number
    yearRangeStart?: number
    yearRangeEnd?: number
  }): Promise<HighYieldUnitsResponse> {
    const response = await apiClient.get<HighYieldUnitsResponse>(
      `${API_BASE}/dashboard/high-yield-units`,
      { params }
    )
    return response.data
  }

  async getTopicTrendsSummary(params?: YearRangeParams): Promise<TopicTrendsSummaryResponse> {
    const response = await apiClient.get<TopicTrendsSummaryResponse>(
      `${API_BASE}/dashboard/topic-trends-summary`,
      { params }
    )
    return response.data
  }

  // Subject Analytics Endpoints
  async getSubjectUnitWeightage(
    subjectCode: string,
    params?: YearRangeParams & {
      sortBy?: 'totalQuestions' | 'importanceScore' | 'avgQuestionsPerExam'
      sortOrder?: 'asc' | 'desc'
    }
  ): Promise<SubjectUnitWeightageResponse> {
    const response = await apiClient.get<SubjectUnitWeightageResponse>(
      `${API_BASE}/subjects/${subjectCode}/unit-weightage`,
      { params }
    )
    return response.data
  }

  async getSubjectDifficultyTrends(
    subjectCode: string,
    params?: YearRangeParams
  ): Promise<SubjectDifficultyTrendsResponse> {
    const response = await apiClient.get<SubjectDifficultyTrendsResponse>(
      `${API_BASE}/subjects/${subjectCode}/difficulty-trends`,
      { params }
    )
    return response.data
  }

  // Unit Analytics Endpoints
  async getUnitTopicBreakdown(
    unitCode: string,
    params?: YearRangeParams & {
      sortBy?: 'totalQuestions' | 'importanceScore' | 'appearanceFrequency'
    }
  ): Promise<UnitTopicBreakdownResponse> {
    const response = await apiClient.get<UnitTopicBreakdownResponse>(
      `${API_BASE}/units/${unitCode}/topic-breakdown`,
      { params }
    )
    return response.data
  }

  // Exam Analytics Endpoints
  async getExamsList(params?: YearRangeParams): Promise<ExamsListResponse> {
    const response = await apiClient.get<ExamsListResponse>(`${API_BASE}/exams/list`, { params })
    return response.data
  }

  async getExamSummary(testCode: string): Promise<ExamSummaryResponse> {
    const response = await apiClient.get<ExamSummaryResponse>(
      `${API_BASE}/exams/${testCode}/summary`
    )
    return response.data
  }

  // High-Yield Analysis Endpoints
  async getHighYieldTopics(params?: {
    subjectCode?: string
    limit?: number
    yearRangeStart?: number
    yearRangeEnd?: number
  }): Promise<HighYieldTopicsResponse> {
    const response = await apiClient.get<HighYieldTopicsResponse>(
      `${API_BASE}/high-yield/topics`,
      { params }
    )
    return response.data
  }

  async getTopImportanceTopics(params?: {
    subjectCode?: string
    limit?: number
  }): Promise<HighYieldTopicsResponse> {
    const response = await apiClient.get<HighYieldTopicsResponse>(
      `${API_BASE}/topics/top-importance`,
      { params }
    )
    return response.data
  }

  async getHighYieldUnitsDetailed(params?: {
    subjectCode?: string
    limit?: number
    yearRangeStart?: number
    yearRangeEnd?: number
  }): Promise<HighYieldUnitsResponse> {
    const response = await apiClient.get<HighYieldUnitsResponse>(
      `${API_BASE}/high-yield/units`,
      { params }
    )
    return response.data
  }

  // Test Attempts Endpoints
  async getStudentAttempts(
    studentId: string,
    params?: {
      status?: TestAttemptStatus
      page?: number
      size?: number
      sort?: string
    }
  ): Promise<PagedTestAttemptResponse> {
    const response = await apiClient.get<PagedTestAttemptResponse>(
      `${TEST_ATTEMPTS_API_BASE}/student/${encodeURIComponent(studentId)}`,
      { params }
    )
    return response.data
  }

  // Trending Topics Endpoints
  async getIncreasingTopics(params?: YearRangeParams & {
    limit?: number
    subjectCode?: string
  }): Promise<TrendingTopicsResponse> {
    const response = await apiClient.get<TrendingTopicsResponse>(
      `${API_BASE}/trends/increasing-topics`,
      { params }
    )
    return response.data
  }

  async getDecreasingTopics(params?: YearRangeParams & {
    limit?: number
    subjectCode?: string
  }): Promise<TrendingTopicsResponse> {
    const response = await apiClient.get<TrendingTopicsResponse>(
      `${API_BASE}/trends/decreasing-topics`,
      { params }
    )
    return response.data
  }

  async getYearOverYearComparison(params?: YearRangeParams): Promise<YearOverYearComparisonResponse> {
    const response = await apiClient.get<YearOverYearComparisonResponse>(
      `${API_BASE}/trends/year-over-year-comparison`,
      { params }
    )
    return response.data
  }

  // Predictions Endpoints
  async getMustStudyTopics(params?: YearRangeParams & {
    limit?: number
    subjectCode?: string
  }): Promise<MustStudyTopicsResponse> {
    const response = await apiClient.get<MustStudyTopicsResponse>(
      `${API_BASE}/predictions/must-study-topics`,
      { params }
    )
    return response.data
  }

  async getNextExamPredictions(params?: YearRangeParams): Promise<NextExamPredictionsResponse> {
    const response = await apiClient.get<NextExamPredictionsResponse>(
      `${API_BASE}/predictions/next-exam`,
      { params }
    )
    return response.data
  }

  // Unit Detail Endpoints
  async getUnitSummary(
    unitCode: string,
    params?: YearRangeParams
  ): Promise<UnitSummaryResponse> {
    const response = await apiClient.get<UnitSummaryResponse>(
      `${API_BASE}/units/${unitCode}/summary`,
      { params }
    )
    return response.data
  }

  async getUnitExamTimeline(
    unitCode: string,
    params?: YearRangeParams
  ): Promise<UnitExamTimelineResponse> {
    const response = await apiClient.get<UnitExamTimelineResponse>(
      `${API_BASE}/units/${unitCode}/exam-timeline`,
      { params }
    )
    return response.data
  }

  // Topic Detail Endpoints
  async getTopicDetailedStats(
    topicCode: string,
    params?: YearRangeParams
  ): Promise<TopicDetailedStatsResponse> {
    const response = await apiClient.get<TopicDetailedStatsResponse>(
      `${API_BASE}/topics/${topicCode}/detailed-stats`,
      { params }
    )
    return response.data
  }

  async getTopicExamTimeline(
    topicCode: string,
    params?: YearRangeParams
  ): Promise<TopicExamTimelineResponse> {
    const response = await apiClient.get<TopicExamTimelineResponse>(
      `${API_BASE}/topics/${topicCode}/exam-timeline`,
      { params }
    )
    return response.data
  }

  // Comparison Endpoints
  async compareTopics(request: TopicComparisonRequest): Promise<TopicComparisonResponse> {
    const response = await apiClient.post<TopicComparisonResponse>(
      `${API_BASE}/comparisons/topic-comparison`,
      request
    )
    return response.data
  }

  // Exam Coverage Endpoints
  async getExamTopicCoverage(testCode: string): Promise<ExamTopicCoverageResponse> {
    const response = await apiClient.get<ExamTopicCoverageResponse>(
      `${API_BASE}/exams/${testCode}/topic-coverage`
    )
    return response.data
  }
}

export default new AnalyticsService()
